import { useState } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { useRef } from "react";
import { SITE } from "../data/site";
import { Plus, ArrowUpRight } from "lucide-react";

function ServiceRow({ svc, i, open, onToggle }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15%" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: (i % 4) * 0.06, ease: [0.65, 0, 0.35, 1] }}
      className={`group border-t border-white/10 ${
        i === SITE.services.length - 1 ? "border-b" : ""
      }`}
      data-testid={`service-row-${svc.key}`}
    >
      <button
        onClick={() => onToggle(svc.key)}
        className="w-full grid grid-cols-12 gap-4 md:gap-8 items-center py-6 md:py-8 px-1 text-left hover:bg-white/[0.02] transition-colors duration-500"
        aria-expanded={open}
        data-testid={`service-toggle-${svc.key}`}
      >
        <div className="col-span-2 md:col-span-1 font-mono text-[11px] tracking-[0.3em] text-[var(--gold)]">
          {svc.no}
        </div>
        <div className="col-span-8 md:col-span-5">
          <h3 className="font-display text-3xl md:text-5xl leading-[1.02] tracking-[-0.015em] transition-transform duration-500 group-hover:translate-x-1">
            {svc.title}
          </h3>
        </div>
        <div className="hidden md:block md:col-span-5 text-[14px] leading-[1.55] text-[var(--ink-dim)]">
          {svc.lead}
        </div>
        <div className="col-span-2 md:col-span-1 flex justify-end">
          <span
            className={`w-11 h-11 rounded-full border border-white/15 flex items-center justify-center transition-all duration-500 ${
              open
                ? "rotate-45 border-[var(--gold)] bg-[var(--gold)] text-[var(--bg)]"
                : "group-hover:border-[var(--gold)] group-hover:text-[var(--gold)]"
            }`}
          >
            <Plus size={16} />
          </span>
        </div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.55, ease: [0.65, 0, 0.35, 1] }}
            className="overflow-hidden"
            data-testid={`service-panel-${svc.key}`}
          >
            <div className="grid grid-cols-12 gap-4 md:gap-8 pb-10 md:pb-14 pt-2">
              <div className="col-span-12 md:col-span-6 md:col-start-2 text-[15px] md:text-[16px] leading-[1.6] text-[var(--ink-dim)] md:hidden">
                {svc.lead}
              </div>
              <ul className="col-span-12 md:col-span-7 md:col-start-2 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
                {svc.items.map((it, idx) => (
                  <motion.li
                    key={it}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + idx * 0.04, duration: 0.5 }}
                    className="flex items-center gap-4 text-[15px] leading-tight text-[var(--ink)] border-b border-white/5 pb-3"
                  >
                    <span className="font-mono text-[10px] text-[var(--ink-mute)] w-6">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span>{it}</span>
                  </motion.li>
                ))}
              </ul>
              <div className="hidden md:flex md:col-span-3 md:col-start-10 items-start justify-end">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[var(--ink-dim)] hover:text-[var(--gold)] transition-colors duration-300"
                >
                  Engage this practice <ArrowUpRight size={14} />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function Services() {
  const [openKey, setOpenKey] = useState("omni");
  const toggle = (k) => setOpenKey((c) => (c === k ? null : k));

  return (
    <section
      id="services"
      className="relative py-24 md:py-40 px-6 md:px-10 bg-[var(--bg-2)]/40"
      data-testid="services-section"
    >
      <div className="mx-auto max-w-[1600px]">
        <div className="grid grid-cols-12 gap-6 items-end mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-7">
            <div className="flex items-center gap-4 text-[11px] tracking-[0.32em] uppercase text-[var(--ink-dim)] mb-6">
              <span className="w-10 h-px bg-[var(--gold)]" />
              <span className="text-[var(--gold)] font-mono">03</span>
              <span>Practices</span>
            </div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.98] tracking-[-0.02em]">
              Eleven disciplines.
              <br />
              <span className="italic text-[var(--gold)] font-light">One</span> operating
              partner.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9">
            <p className="text-[15px] md:text-[16px] leading-[1.6] text-[var(--ink-dim)]">
              Composable engagements. Commission a single practice or the full
              360°. Every mandate is led by senior partners, never delegated.
            </p>
          </div>
        </div>

        <div>
          {SITE.services.map((svc, i) => (
            <ServiceRow
              key={svc.key}
              svc={svc}
              i={i}
              open={openKey === svc.key}
              onToggle={toggle}
            />
          ))}
        </div>

        <div className="mt-14 md:mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="text-[13px] tracking-[0.2em] uppercase text-[var(--ink-mute)] max-w-md">
            Not sure which practice fits? We often begin with a single day
            diagnostic.
          </p>
          <a
            href="#contact"
            className="group inline-flex items-center gap-3 border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--bg)] transition-colors duration-500 px-6 py-4 rounded-full text-[12px] tracking-[0.28em] uppercase"
            data-testid="services-cta"
          >
            Book a diagnostic
            <ArrowUpRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}
